/**
 * ChainExplorerPanel.tsx
 * Admin → Test Mapping — read-only chain explorer, embedded in the detail
 * pane: shows the selected catalog item's TaskType recipe (the chain that
 * actually generates schedule tasks) with each task type's skill chip,
 * 24/7 badge, and machine count / minutes.
 *
 * Data comes from the EXISTING taxonomy endpoints (services/scheduleTaxonomy):
 *   TestCatalogItemTaskType rows  → the recipe
 *   task-types + skills           → skill names
 *   task-types/:key/capabilities  → machines + minutes
 * Editing recipes/skills/machines stays in the Scheduling Taxonomy admin
 * (Admin → Test Catalogue) — this panel links across.
 */

import { useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, Divider, Link as MuiLink, Paper,
  Typography, alpha, useTheme,
} from '@mui/material';
import { Build } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  fetchSkills,
  fetchTaskTypes,
  fetchTestCatalogTaskTypes,
  fetchTaskTypeCapabilities,
} from '../../../services/scheduleTaxonomy';
import { ROUTES } from '../../../constants/routes';
import type { MappingStudioCatalogItem } from '../../../services/testMappingService';

interface RecipeRow {
  taskTypeKey: string;
  name: string;
  skillName: string | null;
  runMode: 'attended' | 'continuous';
  machineCount: number;
  /** Human summary of machine minutes across capable machines (or fallback). */
  minutesLabel: string | null;
}

interface ChainExplorerPanelProps {
  catalogItem: MappingStudioCatalogItem;
}

export const ChainExplorerPanel = ({ catalogItem }: ChainExplorerPanelProps) => {
  const theme = useTheme();
  const [rows, setRows] = useState<RecipeRow[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(false);

    (async () => {
      const [recipe, taskTypes, skills] = await Promise.all([
        fetchTestCatalogTaskTypes(),
        fetchTaskTypes(),
        fetchSkills(),
      ]);
      const mine = recipe.filter((r) => r.TestCatalogItemKey === catalogItem.TestCatalogItemKey);
      const skillByKey = new Map(skills.map((s) => [s.SkillKey, s.Name]));
      const taskTypeByKey = new Map(taskTypes.map((t) => [t.TaskTypeKey, t]));

      const built = await Promise.all(mine.map(async (r): Promise<RecipeRow> => {
        const tt = taskTypeByKey.get(r.TaskTypeKey);
        let machineCount = 0;
        let minutesLabel: string | null = null;
        try {
          const caps = await fetchTaskTypeCapabilities(r.TaskTypeKey);
          machineCount = caps.length;
          const minutes = caps
            .map((c) => c.MachineMinutes)
            .filter((m): m is number => m !== null);
          if (minutes.length > 0) {
            const lo = Math.min(...minutes);
            const hi = Math.max(...minutes);
            minutesLabel = lo === hi ? `${lo} min` : `${lo}–${hi} min`;
          } else if (tt?.DefaultMinutes != null) {
            minutesLabel = `${tt.DefaultMinutes} min (default)`;
          }
        } catch {
          // capabilities are additive info — the recipe row still renders
        }
        return {
          taskTypeKey: r.TaskTypeKey,
          name: r.TaskTypeName,
          skillName: tt?.SkillKey ? skillByKey.get(tt.SkillKey) ?? null : null,
          runMode: r.RunMode,
          machineCount,
          minutesLabel,
        };
      }));

      if (!cancelled) setRows(built);
    })().catch(() => {
      if (!cancelled) setError(true);
    });

    return () => { cancelled = true; };
  }, [catalogItem.TestCatalogItemKey]);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: alpha(theme.palette.divider, 0.5),
        p: 1.5,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
        {catalogItem.TestName}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Task-type recipe (what schedule generation creates for each mapped work-order line)
      </Typography>
      <Divider sx={{ my: 1 }} />

      {error && (
        <Typography variant="body2" color="text.secondary">
          Could not load the recipe — try again.
        </Typography>
      )}
      {!error && rows === null && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {rows !== null && rows.length === 0 && (
        <Typography variant="body2" sx={{ color: theme.schedule.conflictSoft, fontSize: '0.8rem' }}>
          No recipe — work-order lines mapped here will NOT generate schedule tasks until
          task types are added in the Scheduling Taxonomy.
        </Typography>
      )}

      {rows?.map((row) => (
        <Box
          key={row.taskTypeKey}
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 1.5,
            border: `1px solid ${theme.border.color}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
            {row.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {row.skillName ? (
              <Chip
                size="small"
                label={row.skillName}
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  backgroundColor: alpha(theme.schedule.employeeAlloc, 0.14),
                  color: theme.schedule.employeeAlloc,
                }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                no skill required
              </Typography>
            )}
            {row.runMode === 'continuous' && (
              <Chip
                size="small"
                label="24/7"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  backgroundColor: alpha(theme.schedule.continuous, 0.14),
                  color: theme.schedule.continuous,
                }}
              />
            )}
            <Typography
              variant="caption"
              sx={{ fontSize: '0.62rem', color: theme.schedule.machineTime, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              <Build sx={{ fontSize: 11 }} />
              {row.machineCount} machine{row.machineCount === 1 ? '' : 's'}
              {row.minutesLabel ? ` · ${row.minutesLabel}` : ''}
            </Typography>
          </Box>
        </Box>
      ))}

      <Divider sx={{ my: 1 }} />
      <MuiLink
        component={RouterLink}
        to={ROUTES.ADMIN_TEST_CATALOG}
        variant="body2"
        sx={{ fontSize: '0.78rem', fontWeight: 600 }}
      >
        Edit recipe in Scheduling Taxonomy →
      </MuiLink>
    </Paper>
  );
};
